import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Listbox, Switch } from '@headlessui/react';
import moment from 'moment';
import Compressor from 'compressorjs';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import {
	PencilIcon,
	PlusIcon,
	ArrowUpTrayIcon,
	ArrowDownTrayIcon,
	FolderOpenIcon,
	ChartBarIcon,
} from '@heroicons/react/24/outline';

import BetaAttributes from '../components/Attributes';

import {
	commentOnTicket,
	getTicket,
	updateTicket,
	addTicketDocuments,
	deleteTicketDocument,
	getTicketChecklist,
	createChecklistItem,
	updateChecklistItem,
	reorderChecklistItems,
	deleteChecklistItem,
	getWorkOrders,
	updateWorkOrderStatus,
} from '../api';
import { getTeam } from '../api/company';
import { getModules, getModuleCategories } from '../api/modules';
import { getLocations } from '../api/locations';

import {
	StackableModal,
	AttachmentList,
	InlineSelect,
	ImageGallery,
	getNonImageAttachments,
	TaskList,
} from '../components/nextgen';
import { classNames, getUserFullName } from '../components/helpers/functions';
import Badge from '../components/Badge';

import {
	AttributeRow,
	TemplateDropdown,
	TemplateField,
	TemplateTextarea,
} from '../components/Attributes/Attributes_old';
import Button from '../components/Button';

import LoadingSkeleton from '../components/Shell/LoadingSkeleton';
import { AvatarGenerated } from '../components/Avatar';
import Tabs from '../components/Tabs';

import useModal from '../hooks/useModal';
import useLocal from '../hooks/useLocal';
import useAsync from '../hooks/useAsync';
import useSettings from '../hooks/useSettings';
import { StatusBadge, TextStack, AssetInfoCard } from '../components/nextgen';

const MODAL_NAME = 'ticketOverview';

const Editable = ({ children, disabled, onClick, cursor = 'pointer' }) => (
	<div
		className={classNames(
			`group flex`,
			disabled ? 'cursor-default' : `cursor-${cursor}`,
		)}
		onClick={() => typeof onClick === 'function' && !disabled && onClick()}
	>
		{children}
		{!disabled && (
			<div className="ml-2 opacity-0 group-hover:opacity-100 transistion duration-200 ease-in-out flex items-center">
				<PencilIcon className="w-3 h-3 text-gray-400" />
			</div>
		)}
	</div>
);

const STATUS_OPTIONS = [
	{
		name: 'Incoming',
		key: 'incoming',
		description:
			'Initial ticket status.  This is used to keep track of new tickets',
	},
	{
		name: 'In Progress',
		key: 'inprogress',
		description: 'Ticket is being actively worked on',
	},
	{
		name: 'Done',
		key: 'done',
		description: 'Ticket has been successfully completed',
	},
	{
		name: 'Closed',
		key: 'closed',
		description: 'Ticket is not completed and closed',
	},
];

const generateLineItem = (ticket) => {
	const name = `${ticket?.addedBy?.firstName} ${ticket?.addedBy?.lastName}`;
	const updated =
		ticket?.data_type === 'object'
			? ticket?.data
				? JSON.parse(ticket?.data)
				: null
			: ticket?.data;

	if (ticket?.type === 'ticket.created') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span> created the
				ticket
			</>
		);
	}
	if (ticket?.type === 'ticket.done') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span> marked this
				ticket as Done
			</>
		);
	}
	if (ticket?.type === 'ticket.closed') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span> closed this
				ticket
			</>
		);
	}

	if (
		ticket?.type === 'severity.updated' ||
		ticket?.type === 'status.updated' ||
		ticket?.type === 'title.updated' ||
		ticket?.type === 'description.updated'
	) {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span>
				{` updated the ${updated.key}`}
			</>
		);
	}

	if (ticket?.type === 'assignedTo.updated') {
		return (
			<>
				<span className="font-medium text-gray-900">{name}</span>
				{` assigned this ticket`}
			</>
		);
	}
	return null;
};

/**
 * Parse a handlebars-like template and replace variables with asset values.
 * Supports: serialNumber, location, tag, alias, and custom attribute keys.
 */
const parseAssetTemplate = (template, asset) => {
	if (!template) return null;

	const location = asset.location?.name || asset.locationName || '';

	const values = {
		serialNumber: asset.serialNumber || '',
		location: location,
		tag: asset.tag || '',
		alias: asset.alias || '',
	};

	if (asset.customAttributes) {
		Object.entries(asset.customAttributes).forEach(([key, value]) => {
			if (typeof value === 'string' || typeof value === 'number') {
				values[key] = value;
			}
		});
	}

	Object.entries(asset).forEach(([key, value]) => {
		if (
			!values[key] &&
			(typeof value === 'string' || typeof value === 'number')
		) {
			values[key] = value;
		}
	});

	return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, variable) => {
		const value = values[variable];
		return value !== undefined && value !== null && value !== ''
			? String(value)
			: '';
	});
};

const TicketOverview = () => {
	const descriptionTextareaRef = useRef();

	const navigate = useNavigate();

	const { open, setClose } = useModal(MODAL_NAME);

	const { setOpen: onOpenDeleteModal, setClose: onCloseDeleteModal } =
		useModal('deleteModal');

	const { setOpen: openCreateWorkOrder } = useModal('createWorkOrder');
	const { setOpen: openWorkOrderOverview } = useModal('workOrderOverview');
	const { setOpen: openDocumentPicker } = useModal('documentPicker');
	const { setOpen: openDocumentPreview } = useModal('documentPreview');
	const { setOpen: openEditTicket } = useModal('editTicket');
	const { setOpen: openSelectLocation } = useModal('selectLocation');
	const { setOpen: openAddCategory } = useModal('addCategoryModal');
	const { setOpen: openViewContact } = useModal('viewContact');
	const { setOpen: openContract } = useModal('contractOverview');
	const { setOpen: openStatistics } = useModal('statisticsModal');
	const { setOpen: openTaskDetail } = useModal('taskDetail');

	const [form, setForm] = useState({});
	const [isEditingModalOpen, setEditingModalOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [showUploadOptions, setShowUploadOptions] = useState(false);
	const fileInputRef = useRef(null);

	const { get } = useLocal();
	const user = get('user');

	const settings = useSettings();
	const assetDisplayTemplate = settings.get('assetDisplay');

	const handleClose = (path) => {
		setClose();
		if (typeof open?.onClose === 'function') {
			open?.onClose(path);
		}
	};

	// Get Ticket
	const {
		value: ticket,
		status,
		execute: refreshTicket,
	} = useAsync(getTicket, { immediate: false });

	const { execute: onComment, status: commentStatus } = useAsync(
		commentOnTicket,
		{ immediate: false },
	);

	const { execute: onUpdate, status: updating } = useAsync(updateTicket, {
		immediate: false,
	});

	const { value: team, status: gettingTeam } = useAsync(getTeam);

	const { value: modules, execute: onRefreshModules } = useAsync(getModules, {
		immediate: false,
	});

	const ticketsModule = modules?.rows?.find((m) => m.key === 'default:ticket');

	const { value: locations, execute: onRefreshLocations } = useAsync(
		getLocations,
		{ immediate: false },
	);

	const { value: categories, execute: onRefreshCategories } = useAsync(
		getModuleCategories,
		{ immediate: false },
	);

	const { execute: onUploadDocuments } = useAsync(addTicketDocuments, {
		immediate: false,
	});

	// Checklist hooks
	const { value: checklistItems, execute: refreshChecklist } = useAsync(
		getTicketChecklist,
		{ immediate: false },
	);

	const { execute: onCreateChecklistItem } = useAsync(createChecklistItem, {
		immediate: false,
	});

	const { execute: onUpdateChecklistItem } = useAsync(updateChecklistItem, {
		immediate: false,
	});

	const { execute: onReorderChecklistItems } = useAsync(reorderChecklistItems, {
		immediate: false,
	});

	const { execute: onDeleteChecklistItem } = useAsync(deleteChecklistItem, {
		immediate: false,
	});

	// Work orders linked to this ticket
	const { value: workOrdersData, execute: refreshWorkOrders } = useAsync(
		getWorkOrders,
		{ immediate: false },
	);

	// Upload a single file immediately
	const uploadFile = (file) => {
		setIsUploading(true);
		onUploadDocuments(
			{
				id: open?.id,
				files: [file],
			},
			() => {
				setIsUploading(false);
				refreshTicket({ id: open?.id }, null, { silent: true });
			},
		);
	};

	// File upload handler - uploads immediately
	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		if (file.type.startsWith('image/')) {
			setIsUploading(true);
			new Compressor(file, {
				quality: 0.6,
				maxWidth: 1920,
				maxHeight: 1920,
				success: (compressedBlob) => {
					const compressedFile = new File([compressedBlob], file.name, {
						type: compressedBlob.type,
					});
					uploadFile(compressedFile);
				},
				error: () => {
					uploadFile(file);
				},
			});
		} else {
			uploadFile(file);
		}

		e.target.value = '';
	};

	useEffect(() => {
		if (open?.id) {
			refreshTicket({ id: open?.id });
			refreshChecklist({ id: open?.id });
			refreshWorkOrders({ ticketId: open?.id });
			onRefreshModules();
			onRefreshLocations();
		}
	}, [open?.id]);

	// Fetch categories when ticketsModule is available
	useEffect(() => {
		if (ticketsModule?.id) {
			onRefreshCategories({ id: ticketsModule.id });
		}
	}, [ticketsModule?.id]);

	useEffect(() => {
		if (ticket) {
			setForm({
				...form,
				description: ticket?.description,
				subject: ticket?.subject,
				status: ticket?.status,
				severity: ticket?.severity,
				assignedTo: ticket?.assigned_to || 'unassigned',
				categoryId: ticket?.category?.id || null,
				locationId: ticket?.location?.id || null,
			});
			descriptionTextareaRef?.current?.focus();
		}
	}, [ticket]);

	const SeverityMapping = {
		minor: {
			label: 'Minor',
			value: 'minor',
			badgeValue: 'caution',
		},
		major: {
			label: 'Major',
			value: 'major',
			badgeValue: 'warning',
		},
		critical: {
			label: 'Critical',
			value: 'critical',
			badgeValue: 'error',
		},
	};

	const isUpdateDisabled = () => {
		// Handle special cases where form key differs from modal name
		if (isEditingModalOpen === 'location') {
			return ticket?.location?.id === form?.locationId;
		}
		if (isEditingModalOpen === 'category') {
			return ticket?.category?.id === form?.categoryId;
		}
		// Default behavior for other fields
		return (
			!form[isEditingModalOpen] ||
			(isEditingModalOpen &&
				ticket &&
				ticket[isEditingModalOpen] === form[isEditingModalOpen])
		);
	};

	const modalTitle = `${ticket?.ticketCode ? `${ticket?.ticketCode}: ` : ''} ${
		ticket?.subject || ticket?.consumable || 'No Title'
	}`;

	// Export ticket as PDF using print dialog
	const handleExportPdf = () => {
		const printContent = `
			<!DOCTYPE html>
			<html>
			<head>
				<title>Ticket ${ticket?.ticketCode || ''}</title>
				<style>
					body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
					.header { display: flex; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #e5e7eb; }
					.logo { height: 40px; width: auto; }
					h1 { font-size: 24px; margin-bottom: 8px; }
					.ticket-code { color: #6b7280; font-size: 14px; margin-bottom: 24px; }
					.section { margin-bottom: 24px; }
					.section-title { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
					.section-content { font-size: 14px; color: #374151; }
					.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
					.badge { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; }
					.status-open { background: #fef2f2; color: #dc2626; }
					.status-inprogress { background: #eff6ff; color: #2563eb; }
					.status-done { background: #f0fdf4; color: #16a34a; }
					.status-closed { background: #f3f4f6; color: #6b7280; }
					.priority-critical { background: #fef2f2; color: #dc2626; }
					.priority-major { background: #fff7ed; color: #ea580c; }
					.priority-minor { background: #fefce8; color: #ca8a04; }
					.description { background: #f9fafb; padding: 16px; border-radius: 8px; white-space: pre-wrap; }
					.comment { border-left: 3px solid #e5e7eb; padding-left: 16px; margin-bottom: 16px; }
					.comment-author { font-weight: 500; }
					.comment-date { color: #9ca3af; font-size: 12px; }
					.task-list { list-style: none; padding: 0; margin: 0; }
					.task-item { display: flex; align-items: flex-start; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
					.task-item:last-child { border-bottom: none; }
					.task-checkbox { width: 16px; height: 16px; border: 2px solid #d1d5db; border-radius: 4px; flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; }
					.task-checkbox.completed { background: #4f46e5; border-color: #4f46e5; }
					.task-checkbox.completed::after { content: '✓'; color: white; font-size: 10px; font-weight: bold; }
					.task-content { flex: 1; }
					.task-content.completed { color: #9ca3af; text-decoration: line-through; }
					.task-assignee { font-size: 11px; color: #6b7280; margin-top: 2px; }
					.footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af; }
					@media print { body { padding: 20px; } }
				</style>
			</head>
			<body>
				<div class="header">
					<img src="${window.location.origin}/scipods-logo-mark.png" alt="SciPods" class="logo" />
				</div>
				<h1>${ticket?.subject || 'No subject'}</h1>
				<div class="ticket-code">#${ticket?.ticketCode || ''}</div>

				<div class="grid">
					<div class="section">
						<div class="section-title">Status</div>
						<div class="section-content">
							<span class="badge status-${ticket?.status}">${ticket?.status === 'incoming' ? 'Open' : ticket?.status === 'inprogress' ? 'In Progress' : ticket?.status === 'done' ? 'Resolved' : 'Closed'}</span>
						</div>
					</div>
					<div class="section">
						<div class="section-title">Priority</div>
						<div class="section-content">
							<span class="badge priority-${ticket?.severity}">${ticket?.severity ? ticket.severity.charAt(0).toUpperCase() + ticket.severity.slice(1) : 'Not set'}</span>
						</div>
					</div>
					<div class="section">
						<div class="section-title">Category</div>
						<div class="section-content">${ticket?.category?.name || 'Uncategorized'}</div>
					</div>
					<div class="section">
						<div class="section-title">Location</div>
						<div class="section-content">${ticket?.location?.name || 'No location'}</div>
					</div>
					<div class="section">
						<div class="section-title">Assigned to</div>
						<div class="section-content">${ticket?.assignedTo ? getUserFullName(ticket.assignedTo) : 'Unassigned'}</div>
					</div>
					<div class="section">
						<div class="section-title">Reported by</div>
						<div class="section-content">${ticket?.createdBy ? getUserFullName(ticket.createdBy) : 'Unknown'}</div>
					</div>
					<div class="section">
						<div class="section-title">Created</div>
						<div class="section-content">${moment(ticket?.createdAt).format('MMM D, YYYY [at] h:mmA')}</div>
					</div>
					<div class="section">
						<div class="section-title">Last Updated</div>
						<div class="section-content">${moment(ticket?.updatedAt).format('MMM D, YYYY [at] h:mmA')}</div>
					</div>
				</div>

				${
					ticket?.description
						? `
				<div class="section">
					<div class="section-title">Description</div>
					<div class="description">${ticket.description}</div>
				</div>
				`
						: ''
				}

				${
					ticket?.asset
						? `
				<div class="section">
					<div class="section-title">Related Asset</div>
					<div class="section-content">
						${ticket.asset.manufacturer?.name || ticket.asset.manufacturerName || ''} ${ticket.asset.model?.name || ticket.asset.modelName || ''}<br/>
						<span style="color: #6b7280;">S/N: ${ticket.asset.serialNumber || 'N/A'}</span>
					</div>
				</div>
				`
						: ''
				}

				${
					checklistItems?.length > 0
						? `
				<div class="section">
					<div class="section-title">Tasks (${checklistItems.filter((t) => t.completed).length}/${checklistItems.length} completed)</div>
					<ul class="task-list">
						${checklistItems
							.map(
								(task) => `
						<li class="task-item">
							<div class="task-checkbox ${task.completed ? 'completed' : ''}"></div>
							<div class="task-content ${task.completed ? 'completed' : ''}">
								${task.content}
								${task.assignedToUser ? `<div class="task-assignee">Assigned to: ${task.assignedToUser.firstName || ''} ${task.assignedToUser.lastName || ''}</div>` : ''}
							</div>
						</li>
					`,
							)
							.join('')}
					</ul>
				</div>
				`
						: ''
				}

				${
					ticket?.activities?.filter((a) => a.type === 'comment.created')
						?.length > 0
						? `
				<div class="section">
					<div class="section-title">Comments</div>
					${ticket.activities
						.filter((a) => a.type === 'comment.created')
						.map(
							(comment) => `
						<div class="comment">
							<div><span class="comment-author">${comment.addedBy?.firstName || ''} ${comment.addedBy?.lastName || ''}</span> <span class="comment-date">${moment(comment.createdAt).format('MMM D, YYYY [at] h:mmA')}</span></div>
							<div style="margin-top: 4px;">${comment.data || ''}</div>
						</div>
					`,
						)
						.join('')}
				</div>
				`
						: ''
				}

				<div class="footer">
					Exported on ${moment().format('MMMM D, YYYY [at] h:mmA')}
				</div>
			</body>
			</html>
		`;

		const printWindow = window.open('', '_blank');
		printWindow.document.write(printContent);
		printWindow.document.close();
		printWindow.focus();
		setTimeout(() => printWindow.print(), 250);
	};

	const isBare = !!open?.isBare;
	const isResolved = ticket?.status === 'done' || ticket?.status === 'closed';

	return (
		<>
			<StackableModal open={!!open} onClose={() => handleClose()} size="4xl">
				{status === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left -mt-4 lg:mt-0">
						{/* Mobile close button - only show below sm, Modal has its own close button at sm+ */}
						<div className="sm:hidden flex justify-end -mr-2 -mb-4 col-span-full">
							<button
								type="button"
								className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
								onClick={() => handleClose()}
							>
								<span className="sr-only">Close</span>
								<svg
									className="h-5 w-5"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>

						<div className="lg:col-span-2">
							<div className="space-x-2">
								<StatusBadge rounded="sm">{`Ticket: #${ticket?.ticketCode}`}</StatusBadge>
								<StatusBadge status={ticket?.status} />
							</div>

							<div className="text-xl font-semibold mt-6">
								<div className="flex items-center text-gray-700">
									{ticket?.subject}
								</div>
							</div>

							<div className="mt-6 text-xs text-gray-500 font-medium text-muted-foreground uppercase tracking-wider text-left">
								Status
							</div>

							{/* Status Quick Actions */}
							<div className="flex flex-wrap gap-2 mt-3">
								{[
									{ key: 'incoming', label: 'Open' },
									{ key: 'inprogress', label: 'In Progress' },
									{ key: 'done', label: 'Resolved' },
									{ key: 'closed', label: 'Closed' },
								].map((statusOption) => (
									<button
										key={statusOption.key}
										onClick={() => {
											if (statusOption.key === ticket?.status) return;

											const incompleteTasks = checklistItems?.filter(
												(task) => !task.completed,
											);
											const isClosingStatus =
												statusOption.key === 'done' ||
												statusOption.key === 'closed';

											// Get open work orders (not completed or cancelled)
											const openWorkOrders = (
												workOrdersData?.rows || []
											).filter(
												(wo) => !['completed', 'cancelled'].includes(wo.status),
											);

											// Helper to finalize ticket status update
											const finalizeStatusUpdate = () => {
												onUpdate(
													{
														id: open?.id,
														status: statusOption.key,
														...(statusOption.key === 'done'
															? { completedDate: new Date() }
															: {}),
													},
													() => {
														onCloseDeleteModal();
														refreshTicket({ id: open?.id }, null, {
															silent: true,
														});
														refreshChecklist({ id: open?.id });
														refreshWorkOrders({ ticketId: open?.id });
														if (statusOption.key === 'done') {
															openStatistics({
																entityType: 'ticket',
																id: open?.id,
															});
														}
													},
												);
											};

											// Helper to show work orders modal (called after tasks are handled)
											const showWorkOrdersModal = () => {
												if (isClosingStatus && openWorkOrders.length > 0) {
													onOpenDeleteModal({
														title: `${openWorkOrders.length} open work order${openWorkOrders.length > 1 ? 's' : ''}`,
														description: (form, setForm) => (
															<div className="space-y-3">
																<span>
																	This ticket has {openWorkOrders.length} open
																	work order
																	{openWorkOrders.length > 1 ? 's' : ''}. How
																	would you like to handle{' '}
																	{openWorkOrders.length > 1 ? 'them' : 'it'}?
																</span>
																<div className="mt-3 space-y-2">
																	<label className="flex items-center gap-2 cursor-pointer">
																		<input
																			type="radio"
																			name="workOrderAction"
																			value="completed"
																			checked={
																				form.workOrderAction === 'completed'
																			}
																			onChange={() =>
																				setForm({
																					workOrderAction: 'completed',
																				})
																			}
																			className="h-4 w-4 text-indigo-600"
																		/>
																		<span className="text-sm">
																			Mark as Completed
																		</span>
																	</label>
																	<label className="flex items-center gap-2 cursor-pointer">
																		<input
																			type="radio"
																			name="workOrderAction"
																			value="cancelled"
																			checked={
																				form.workOrderAction === 'cancelled'
																			}
																			onChange={() =>
																				setForm({
																					workOrderAction: 'cancelled',
																				})
																			}
																			className="h-4 w-4 text-indigo-600"
																		/>
																		<span className="text-sm">
																			Mark as Cancelled
																		</span>
																	</label>
																</div>
															</div>
														),
														defaultForm: { workOrderAction: 'completed' },
														confirmText: 'Update Work Orders',
														onConfirm: async (form) => {
															await Promise.all(
																openWorkOrders.map((wo) =>
																	updateWorkOrderStatus({
																		id: wo.id,
																		status: form.workOrderAction,
																	}),
																),
															);
															finalizeStatusUpdate();
														},
														cancelText: 'Leave Open',
														onCancel: () => {
															finalizeStatusUpdate();
														},
													});
												} else {
													finalizeStatusUpdate();
												}
											};

											if (isClosingStatus && incompleteTasks?.length > 0) {
												onOpenDeleteModal({
													title: `${incompleteTasks.length} incomplete task${incompleteTasks.length > 1 ? 's' : ''}`,
													description: (
														<span>
															This ticket has {incompleteTasks.length}{' '}
															incomplete task
															{incompleteTasks.length > 1 ? 's' : ''}. Would you
															like to mark them as complete?
														</span>
													),
													confirmText: 'Complete all tasks',
													onConfirm: async () => {
														// Mark all incomplete tasks as complete
														await Promise.all(
															incompleteTasks.map((task) =>
																onUpdateChecklistItem({
																	id: open?.id,
																	checklistItemId: task.id,
																	completed: true,
																}),
															),
														);
														// Check for open work orders next
														showWorkOrdersModal();
													},
													cancelText: 'Leave incomplete',
													onCancel: () => {
														// Check for open work orders next
														showWorkOrdersModal();
													},
												});
											} else {
												// No incomplete tasks, check for work orders
												showWorkOrdersModal();
											}
										}}
										className={classNames(
											'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
											ticket?.status === statusOption.key
												? 'bg-indigo-600 text-white'
												: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
										)}
									>
										{statusOption.label}
									</button>
								))}
							</div>

							<div className="mt-6 text-xs text-gray-500 font-medium text-muted-foreground uppercase tracking-wider">
								Description{' '}
							</div>

							{ticket?.description ? (
								<>
									<div className="pt-3 text-sm">{ticket?.description}</div>
								</>
							) : (
								<>
									<p className="mt-3 text-sm text-gray-500">
										No description provided
									</p>
								</>
							)}

							{/* Mobile: Key attributes below description */}
							<div className="lg:hidden grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-b pb-6 border-gray-200">
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Priority
									</div>
									<InlineSelect
										value={ticket?.severity || ''}
										options={Object.keys(SeverityMapping).map((key) => ({
											value: key,
											label: SeverityMapping[key].label,
											badgeValue: SeverityMapping[key].value,
										}))}
										onChange={(val) => {
											if (isResolved) return;
											onUpdate({ id: open?.id, severity: val || null }, () =>
												refreshTicket({ id: open?.id }, null, {
													silent: true,
												}),
											);
										}}
										disabled={isResolved}
										placeholder="No priority"
										renderButton={(option) => (
											<StatusBadge
												preset="priority"
												value={option?.badgeValue}
												varient="outline"
											>
												{option?.label || 'No priority'}
											</StatusBadge>
										)}
									/>
								</div>
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Assigned to
									</div>
									<StatusBadge
										className={isResolved ? 'cursor-default' : 'cursor-pointer'}
										onClick={() =>
											!isResolved && setEditingModalOpen('assignedTo')
										}
									>
										{ticket?.assignedTo ? (
											<div
												className={classNames(
													'-ml-2 text-xs flex items-center',
													isResolved ? 'cursor-default' : 'cursor-pointer',
												)}
											>
												<AvatarGenerated
													id={getUserFullName(ticket?.assignedTo)}
													size="xxs"
													className="mr-1.5"
												/>{' '}
												{getUserFullName(ticket?.assignedTo)}
											</div>
										) : (
											'Unassigned'
										)}
									</StatusBadge>
								</div>
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Category
									</div>
									<Listbox
										value={ticket?.category?.id || ''}
										disabled={isResolved}
										onChange={(val) => {
											if (isResolved) return;
											onUpdate({ id: open?.id, categoryId: val || null }, () =>
												refreshTicket({ id: open?.id }, null, {
													silent: true,
												}),
											);
										}}
									>
										<div className="relative">
											<Listbox.Button
												className="flex items-center gap-1"
												disabled={isResolved}
											>
												<span
													className={classNames(
														'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
														isResolved ? 'cursor-default' : 'cursor-pointer',
														'bg-gray-100 text-gray-700',
														!isResolved && 'hover:bg-gray-200',
													)}
												>
													{ticket?.category?.name || 'No category'}
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute left-0 z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
												<Listbox.Option
													value=""
													className={({ active, selected }) =>
														classNames(
															'cursor-pointer select-none relative py-2 pl-3 pr-9',
															active ? 'bg-indigo-50' : '',
															selected ? 'bg-indigo-100' : '',
														)
													}
												>
													{({ selected }) => (
														<div className="flex items-center">
															<span
																className={classNames(
																	'text-gray-500 text-xs',
																	selected && 'font-semibold',
																)}
															>
																No category
															</span>
															{selected && (
																<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
															)}
														</div>
													)}
												</Listbox.Option>
												{categories
													?.sort((a, b) => a.name.localeCompare(b.name))
													?.map((category) => (
														<Listbox.Option
															key={category.id}
															value={category.id}
															className={({ active, selected }) =>
																classNames(
																	'cursor-pointer select-none relative py-2 pl-3 pr-9',
																	active ? 'bg-indigo-50' : '',
																	selected ? 'bg-indigo-100' : '',
																)
															}
														>
															{({ selected }) => (
																<div className="flex items-center text-xs">
																	<span
																		className={selected ? 'font-semibold' : ''}
																	>
																		{category.name}
																	</span>
																	{selected && (
																		<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																	)}
																</div>
															)}
														</Listbox.Option>
													))}
												<div className="border-t border-gray-100 mt-1 pt-1">
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															openAddCategory({
																moduleId: ticketsModule?.id,
																onSubmit: (category) => {
																	console.log('submit:L ', category);
																	onUpdate(
																		{
																			id: ticket?.id,
																			categoryId: category?.id,
																		},
																		() => {
																			refreshTicket({ id: ticket?.id }, null, {
																				silent: true,
																			});
																		},
																	);
																},
															});
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50 font-medium"
													>
														<PlusIcon className="h-4 w-4" />
														Add category
													</button>
												</div>
											</Listbox.Options>
										</div>
									</Listbox>
								</div>
								<div className="text-left">
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider mb-1">
										Location
									</div>
									<span
										className={classNames(
											'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
											isResolved ? 'cursor-default' : 'cursor-pointer',
											'bg-gray-100 text-gray-700',
											!isResolved && 'hover:bg-gray-200',
										)}
										onClick={() => {
											if (isResolved) return;
											openSelectLocation({
												locations,
												selected: ticket?.location?.id,
												onSelect: (location) => {
													onUpdate(
														{ id: ticket?.id, locationId: location?.id },
														() => {
															refreshTicket({ id: ticket?.id }, null, {
																silent: true,
															});
														},
													);
												},
											});
										}}
									>
										{ticket?.location?.name || 'No location'}
									</span>
								</div>
							</div>

							{ticket?.asset && (
								<>
									<div className="mt-6 text-xs text-gray-500 font-medium text-muted-foreground uppercase tracking-wider">
										Asset
									</div>

									<div className="mt-3">
										<AssetInfoCard
											asset={ticket?.asset}
											assetDisplayTemplate={assetDisplayTemplate}
											parseAssetTemplate={parseAssetTemplate}
											onViewAsset={() => {
												handleClose(`/assets/${ticket?.asset?.id}`);
												navigate(`/assets/${ticket?.asset?.id}/overview`);
											}}
											onContractClick={(contract) =>
												openContract({ id: contract.id })
											}
											onVendorClick={(contact) =>
												openViewContact({
													id: contact.id,
													onNavigate: () => setClose(),
												})
											}
										/>
									</div>
								</>
							)}

							<div className="mt-6 flex items-center justify-between">
								<div className="text-xs text-gray-500 font-medium text-muted-foreground uppercase tracking-wider">
									Work Orders
								</div>
								<button
									type="button"
									onClick={() =>
										openCreateWorkOrder({
											ticketId: open?.id,
											asset: ticket?.asset,
											onSuccess: () =>
												refreshWorkOrders({ ticketId: open?.id }),
										})
									}
									className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
								>
									<svg
										className="h-4 w-4"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth="2"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M12 4.5v15m7.5-7.5h-15"
										/>
									</svg>
									Create Work Order
								</button>
							</div>

							<div className="mt-3 space-y-2">
								{workOrdersData?.rows?.length > 0 ? (
									workOrdersData.rows.map((wo) => {
										return (
											<div
												key={wo.id}
												className="flex items-center justify-between rounded-md border p-3 cursor-pointer hover:bg-gray-50"
												onClick={() => {
													openWorkOrderOverview({
														id: wo.id,
														onClose: () =>
															refreshWorkOrders({ ticketId: open?.id }),
													});
												}}
											>
												<div className="min-w-0">
													<TextStack
														primary={wo.title || 'Untitled Work Order'}
														secondary={[
															wo.workOrderCode,
															wo.dueDate
																? moment(wo.dueDate).format('MMM D, YYYY')
																: null,
															wo.assignedToUser
																? getUserFullName(wo.assignedToUser)
																: 'Unassigned',
														].filter(Boolean)}
													/>
												</div>
												<StatusBadge
													status={wo.status}
													{...(wo?.status === 'scheduled'
														? {
																label: wo.dueDate
																	? `Scheduled for ${moment(wo.dueDate).format('MMM D')}`
																	: 'Scheduled',
															}
														: {})}
												/>
											</div>
										);
									})
								) : (
									<p
										className="mt-3 text-sm text-gray-500 "
										onClick={() =>
											openCreateWorkOrder({
												ticketId: open?.id,
												asset: ticket?.asset,
												onSuccess: () =>
													refreshWorkOrders({ ticketId: open?.id }),
											})
										}
									>
										No work orders for this ticket
									</p>
								)}
							</div>

							<section aria-labelledby="attachments-title">
								<div className="mt-6 flex items-center justify-between">
									<div className="text-xs text-gray-500 font-medium text-muted-foreground uppercase tracking-wider">
										Attachments
									</div>
									<div className="relative">
										<button
											type="button"
											onClick={() => setShowUploadOptions(!showUploadOptions)}
											className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
										>
											<PlusIcon className="w-3.5 h-3.5" />
											{isUploading ? 'Uploading...' : 'Add Attachment'}
										</button>

										{/* Upload Options Dropdown */}
										{showUploadOptions && (
											<>
												<div
													className="fixed inset-0 z-40"
													onClick={() => setShowUploadOptions(false)}
												/>
												<div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
													<button
														type="button"
														onClick={() => {
															fileInputRef.current?.click();
															setShowUploadOptions(false);
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
													>
														<ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
														Upload File
													</button>
													<button
														type="button"
														onClick={() => {
															setShowUploadOptions(false);
															openDocumentPicker({
																multiSelect: true,
																onSelect: (selectedDocs) => {
																	// Upload selected documents to ticket
																	if (selectedDocs?.length > 0) {
																		setIsUploading(true);
																		onUploadDocuments(
																			{
																				id: open?.id,
																				documentIds: selectedDocs.map(
																					(doc) => doc.id,
																				),
																			},
																			() => {
																				setIsUploading(false);
																				refreshTicket({ id: open?.id }, null, {
																					silent: true,
																				});
																			},
																		);
																	}
																},
															});
														}}
														className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
													>
														<FolderOpenIcon className="w-4 h-4 text-gray-500" />
														Choose from Library
													</button>
												</div>
											</>
										)}

										{/* Hidden file input */}
										<input
											ref={fileInputRef}
											type="file"
											accept="image/jpeg, image/png, application/pdf, .doc, .docx, .xls, .xlsx"
											onChange={handleFileUpload}
											className="hidden"
										/>
									</div>
								</div>

								{/* Non-image Attachments */}
								<AttachmentList
									attachments={getNonImageAttachments(ticket?.documents)}
									showEmpty={false}
									onClick={(attachment) => openDocumentPreview(attachment)}
									onDelete={(attachment) => {
										onOpenDeleteModal({
											title: 'Remove attachment?',
											description: (
												<span>
													Are you sure you want to remove "{attachment.name}"?
													This action cannot be undone.
												</span>
											),
											confirmText: 'Remove',
											onConfirm: async () => {
												await deleteTicketDocument({
													id: ticket.id,
													documentId: attachment.id,
												});
												onCloseDeleteModal();
												refreshTicket({ id: ticket.id });
											},
										});
									}}
								/>
								{/* Image Gallery */}
								<ImageGallery
									attachments={ticket?.documents}
									className="mt-3"
									onClick={(attachment) => openDocumentPreview(attachment)}
									onDelete={(attachment) => {
										onOpenDeleteModal({
											title: 'Remove image?',
											description: (
												<span>
													Are you sure you want to remove "{attachment.name}"?
													This action cannot be undone.
												</span>
											),
											confirmText: 'Remove',
											onConfirm: async () => {
												await deleteTicketDocument({
													id: ticket.id,
													documentId: attachment.id,
												});
												onCloseDeleteModal();
												refreshTicket({ id: ticket.id });
											},
										});
									}}
								/>

								{/* Empty state when no attachments at all */}
								{(!ticket?.documents || ticket?.documents?.length === 0) && (
									<p className="text-sm text-gray-500 mt-3">No attachments</p>
								)}
							</section>

							{/* Tasks/Checklist Section */}
							<div className="mt-6">
								<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
									Tasks
								</div>
								<TaskList
									items={checklistItems || []}
									team={team?.rows}
									onToggle={(taskId, completed) => {
										onUpdateChecklistItem(
											{
												id: open?.id,
												checklistItemId: taskId,
												completed: !completed,
											},
											() => refreshChecklist({ id: open?.id }),
										);
									}}
									onAssign={(taskId, userId) => {
										onUpdateChecklistItem(
											{
												id: open?.id,
												checklistItemId: taskId,
												assignedTo: userId,
											},
											() => refreshChecklist({ id: open?.id }),
										);
									}}
									onDueDateChange={(taskId, dueDate) => {
										console.log('taskId: ', taskId, dueDate);
										onUpdateChecklistItem(
											{
												id: open?.id,
												checklistItemId: taskId,
												dueDate: dueDate,
											},
											() => refreshChecklist({ id: open?.id }),
										);
									}}
									onDelete={(taskId) => {
										onOpenDeleteModal({
											title: 'Delete task?',
											description: (
												<span>
													Are you sure you want to delete this task? This action
													cannot be undone.
												</span>
											),
											confirmText: 'Delete',
											onConfirm: async () => {
												await onDeleteChecklistItem(
													{ id: open?.id, checklistItemId: taskId },
													() => {
														onCloseDeleteModal();
														refreshChecklist({ id: open?.id });
													},
												);
											},
										});
									}}
									onReorder={(items) => {
										onReorderChecklistItems({ id: open?.id, items }, () =>
											refreshChecklist({ id: open?.id }),
										);
									}}
									onAdd={(title) => {
										onCreateChecklistItem(
											{ id: open?.id, title },
											(createdTask) => {
												refreshChecklist({ id: open?.id });
												// Open task detail modal so user can add more info
												if (createdTask) {
													openTaskDetail({
														task: createdTask,
														team: team?.rows,
														hideToggle: true, // Skip toggle when just created
														onUpdate: async (taskId, updates) => {
															await onUpdateChecklistItem({
																id: open?.id,
																checklistItemId: taskId,
																...updates,
															});
															refreshChecklist({ id: open?.id });
														},
														onToggle: async (taskId, currentCompleted) => {
															await onUpdateChecklistItem({
																id: open?.id,
																checklistItemId: taskId,
																completed: !currentCompleted,
															});
															refreshChecklist({ id: open?.id });
														},
													});
												}
											},
										);
									}}
									onTaskClick={(task) => {
										openTaskDetail({
											task,
											team: team?.rows,
											onUpdate: async (taskId, updates) => {
												await onUpdateChecklistItem({
													id: open?.id,
													checklistItemId: taskId,
													...updates,
												});
												refreshChecklist({ id: open?.id });
											},
											onToggle: async (taskId, currentCompleted) => {
												await onUpdateChecklistItem({
													id: open?.id,
													checklistItemId: taskId,
													completed: !currentCompleted,
												});
												refreshChecklist({ id: open?.id });
											},
											onDelete: async (taskId) => {
												await onDeleteChecklistItem(
													{ id: open?.id, checklistItemId: taskId },
													() => {
														refreshChecklist({ id: open?.id });
													},
												);
											},
										});
									}}
									emptyText="No tasks yet"
								/>
							</div>

							<div className="mt-6 text-xs text-gray-500 font-medium text-muted-foreground uppercase tracking-wider">
								Comments
							</div>

							<>
								{ticket?.activities
									?.filter((activity) => activity.type === 'comment.created')
									?.map((activityItem, activityItemIdx) => (
										<li
											key={activityItem.id}
											className="relative flex gap-x-4 mt-3"
										>
											<div
												className={classNames(
													activityItemIdx === ticket?.activities?.length - 1
														? 'h-6'
														: '-bottom-6',
													'absolute left-0 top-0 flex w-6 justify-center',
												)}
											>
												<div className="w-px bg-gray-200" />
											</div>

											<>
												<AvatarGenerated
													id={getUserFullName(activityItem?.addedByUser)}
													size="xxs"
													className="relative mt-3 h-6 w-6 flex-none rounded-full bg-gray-50"
												/>
												<div className="flex-auto rounded-md px-4 py-2 ring-1 ring-inset ring-gray-200">
													<div className="flex justify-between gap-x-4">
														<div className="py-0.5 text-xs leading-5 text-gray-500">
															<span className="font-medium text-gray-900">
																{`${activityItem?.addedByUser?.firstName} ${activityItem?.addedByUser?.lastName}`}
															</span>{' '}
															commented
														</div>
														<time
															dateTime={activityItem?.createdAt}
															className="flex-none py-0.5 text-xs leading-5 text-gray-500"
														>
															{moment(activityItem?.createdAt).fromNow()}
														</time>
													</div>
													<p className="text-xs text-gray-500 mt-1">
														{activityItem.data}
													</p>
												</div>
											</>
										</li>
									))}

								<div className="mt-3">
									<div className="flex space-x-3">
										<div className="flex-shrink-0 -ml-1">
											<div className="relative">
												<AvatarGenerated id={getUserFullName(user)} size="xs" />
											</div>
										</div>
										<div className="min-w-0 flex-1">
											<TemplateTextarea
												placeholder="Leave a comment"
												dataIndex="comment"
												onChange={(index, comment) =>
													setForm({ ...form, comment })
												}
												value={form?.comment}
											/>
											<div className="mt-6 flex items-center justify-end space-x-4">
												<Button
													onClick={() =>
														onComment(
															{
																id: open?.id,
																comment: form?.comment,
															},
															() => {
																setForm({
																	...form,
																	comment: '',
																});
																refreshTicket({ id: open?.id }, null, {
																	silent: true,
																});
															},
														)
													}
													disabled={!form?.comment}
												>
													Add Comment
												</Button>
											</div>
										</div>
									</div>
								</div>
							</>
						</div>
						{/* Desktop Sidebar - hidden on mobile */}
						<div className="hidden lg:flex lg:col-span-1 flex-col">
							<div className="space-y-4">
								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Priority
									</div>
									<div className="mt-1">
										<InlineSelect
											value={ticket?.severity || ''}
											options={Object.keys(SeverityMapping).map((key) => ({
												value: key,
												label: SeverityMapping[key].label,
												badgeValue: SeverityMapping[key].value,
											}))}
											onChange={(val) => {
												if (isResolved) return;
												onUpdate({ id: open?.id, severity: val || null }, () =>
													refreshTicket({ id: open?.id }, null, {
														silent: true,
													}),
												);
											}}
											disabled={isResolved}
											placeholder="No priority"
											renderButton={(option) => (
												<StatusBadge
													preset="priority"
													value={option?.badgeValue}
													varient="outline"
												>
													{option?.label || 'No priority'}
												</StatusBadge>
											)}
										/>
									</div>
								</div>

								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Assigned to
									</div>
									<div className="mt-1">
										<Listbox
											value={ticket?.assigned_to || ''}
											disabled={isResolved}
											onChange={(val) => {
												if (isResolved) return;
												onUpdate(
													{ id: open?.id, assignedTo: val || null },
													() =>
														refreshTicket({ id: open?.id }, null, {
															silent: true,
														}),
												);
											}}
										>
											<div className="relative">
												<Listbox.Button
													className="flex items-center gap-1"
													disabled={isResolved}
												>
													<span
														className={classNames(
															'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
															isResolved ? 'cursor-default' : 'cursor-pointer',
															'bg-gray-100 text-gray-700',
															!isResolved && 'hover:bg-gray-200',
														)}
													>
														{ticket?.assignedTo ? (
															<span className="flex items-center gap-1.5">
																<AvatarGenerated
																	id={getUserFullName(ticket?.assignedTo)}
																	size="xxs"
																/>
																{getUserFullName(ticket?.assignedTo)}
															</span>
														) : (
															'Unassigned'
														)}
													</span>
												</Listbox.Button>
												<Listbox.Options className="absolute left-0 z-10 mt-1 max-h-60 w-56 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													<Listbox.Option
														value=""
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center">
																<span
																	className={classNames(
																		'text-gray-500 text-xs',
																		selected && 'font-semibold',
																	)}
																>
																	Unassigned
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																)}
															</div>
														)}
													</Listbox.Option>
													{team?.rows
														?.filter(
															(member) => member.active && !member.archived,
														)
														?.map((member) => (
															<Listbox.Option
																key={member.id}
																value={member.id}
																className={({ active, selected }) =>
																	classNames(
																		'cursor-pointer select-none relative py-2 pl-3 pr-9',
																		active ? 'bg-indigo-50' : '',
																		selected ? 'bg-indigo-100' : '',
																	)
																}
															>
																{({ selected }) => (
																	<div className="flex items-center text-xs">
																		<AvatarGenerated
																			id={getUserFullName(member)}
																			size="xxs"
																			className="mr-2"
																		/>
																		<span
																			className={
																				selected ? 'font-semibold' : ''
																			}
																		>
																			{member?.first_name || member?.firstName}{' '}
																			{member?.last_name || member?.lastName}
																		</span>
																		{selected && (
																			<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																		)}
																	</div>
																)}
															</Listbox.Option>
														))}
												</Listbox.Options>
											</div>
										</Listbox>
									</div>
								</div>

								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Category
									</div>
									<div className="mt-1">
										<Listbox
											value={ticket?.category?.id || ''}
											disabled={isResolved}
											onChange={(val) => {
												if (isResolved) return;
												onUpdate(
													{ id: open?.id, categoryId: val || null },
													() =>
														refreshTicket({ id: open?.id }, null, {
															silent: true,
														}),
												);
											}}
										>
											<div className="relative">
												<Listbox.Button
													className="flex items-center gap-1"
													disabled={isResolved}
												>
													<span
														className={classNames(
															'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
															isResolved ? 'cursor-default' : 'cursor-pointer',
															'bg-gray-100 text-gray-700',
															!isResolved && 'hover:bg-gray-200',
														)}
													>
														{ticket?.category?.name || 'No category'}
													</span>
												</Listbox.Button>
												<Listbox.Options className="absolute left-0 z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
													<Listbox.Option
														value=""
														className={({ active, selected }) =>
															classNames(
																'cursor-pointer select-none relative py-2 pl-3 pr-9',
																active ? 'bg-indigo-50' : '',
																selected ? 'bg-indigo-100' : '',
															)
														}
													>
														{({ selected }) => (
															<div className="flex items-center">
																<span
																	className={classNames(
																		'text-gray-500 text-xs',
																		selected && 'font-semibold',
																	)}
																>
																	No category
																</span>
																{selected && (
																	<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																)}
															</div>
														)}
													</Listbox.Option>
													{categories
														?.sort((a, b) => a.name.localeCompare(b.name))
														?.map((category) => (
															<Listbox.Option
																key={category.id}
																value={category.id}
																className={({ active, selected }) =>
																	classNames(
																		'cursor-pointer select-none relative py-2 pl-3 pr-9',
																		active ? 'bg-indigo-50' : '',
																		selected ? 'bg-indigo-100' : '',
																	)
																}
															>
																{({ selected }) => (
																	<div className="flex items-center text-xs">
																		<span
																			className={
																				selected ? 'font-semibold' : ''
																			}
																		>
																			{category.name}
																		</span>
																		{selected && (
																			<CheckIcon className="h-4 w-4 text-indigo-600 ml-auto" />
																		)}
																	</div>
																)}
															</Listbox.Option>
														))}
													<div className="border-t border-gray-100 mt-1 pt-1">
														<button
															type="button"
															onClick={(e) => {
																e.stopPropagation();
																openAddCategory({
																	moduleId: ticketsModule?.id,
																	onSubmit: (category) => {
																		console.log('submit:L ', category);
																		onUpdate(
																			{
																				id: ticket?.id,
																				categoryId: category?.value,
																			},
																			() => {
																				onRefreshCategories({
																					id: ticketsModule.id,
																				});
																				refreshTicket(
																					{ id: ticket?.id },
																					null,
																					{
																						silent: true,
																					},
																				);
																			},
																		);
																	},
																});
															}}
															className="w-full flex items-center gap-2 px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50 font-medium"
														>
															<PlusIcon className="h-4 w-4" />
															Add category
														</button>
													</div>
												</Listbox.Options>
											</div>
										</Listbox>
									</div>
								</div>

								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Location
									</div>
									<div className="mt-1">
										<span
											className={classNames(
												'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
												isResolved ? 'cursor-default' : 'cursor-pointer',
												'bg-gray-100 text-gray-700',
												!isResolved && 'hover:bg-gray-200',
											)}
											onClick={() => {
												if (isResolved) return;
												openSelectLocation({
													locations,
													selected: ticket?.location?.id,
													onSelect: (location) => {
														onUpdate(
															{ id: ticket?.id, locationId: location?.id },
															() => {
																refreshTicket({ id: ticket?.id }, null, {
																	silent: true,
																});
															},
														);
													},
												});
											}}
										>
											{ticket?.location?.name || 'No location'}
										</span>
									</div>
								</div>

								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Reported by
									</div>
									<div className="text-xs mt-1">
										{getUserFullName(ticket?.createdBy) || 'N/A'}
									</div>
								</div>

								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Created on
									</div>
									<div className="text-xs mt-1">
										{moment(ticket?.createdAt).format('MMM D, YYYY [at] h:mmA')}{' '}
										({moment(ticket?.createdAt).fromNow()})
									</div>
								</div>

								{/* Completed on */}
								{ticket?.completedDate && (
									<div>
										<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
											Completed on
										</div>
										<div className="text-xs mt-1">
											{moment(ticket?.completedDate).format(
												'MMM D, YYYY [at] h:mmA',
											)}
										</div>
									</div>
								)}

								<div>
									<div className="text-xxs text-gray-500 font-medium uppercase tracking-wider">
										Last updated
									</div>
									<div className="text-xs mt-1">
										{moment(ticket?.updatedAt).format('MMM D, YYYY [at] h:mmA')}
									</div>
								</div>
							</div>

							{isResolved ? (
								<Button
									type="primary"
									Icon={ChartBarIcon}
									className="mt-8 w-full justify-center"
									onClick={() =>
										openStatistics({ id: ticket?.id, entityType: 'ticket' })
									}
								>
									View Statistics
								</Button>
							) : (
								<Button
									type="primary"
									Icon={PencilIcon}
									className="mt-8 w-full justify-center"
									onClick={() =>
										openEditTicket({
											ticket,
											onSuccess: () => {
												refreshTicket({ id: ticket?.id }, null, {
													silent: true,
												});
											},
											onDeleted: () => {
												handleClose();
											},
										})
									}
								>
									Edit Ticket
								</Button>
							)}

							{/* Actions */}
							<Button
								type="secondary"
								Icon={ArrowDownTrayIcon}
								className="mt-2 w-full justify-center"
								onClick={handleExportPdf}
							>
								Export as PDF
							</Button>

							{/* Audit */}
							<Button
								type="ghost"
								className="mt-2 w-full justify-center"
								onClick={() => {
									handleClose();
									navigate(`/audit-log?entityType=ticket&entityId=${open?.id}`);
								}}
							>
								View Audit Logs
							</Button>
						</div>
					</div>
				)}
			</StackableModal>
			<StackableModal
				open={!!isEditingModalOpen}
				onClose={() => setEditingModalOpen(false)}
			>
				{isEditingModalOpen === 'description' && (
					<>
						<TemplateTextarea
							name="Description"
							placeholder="Enter a description"
							onChange={(index, description) =>
								setForm({ ...form, description })
							}
							value={form?.description}
							autoFocus
						/>
					</>
				)}
				{isEditingModalOpen === 'subject' && (
					<>
						<TemplateField
							name="Title"
							placeholder="Enter brief summary"
							onChange={(index, subject) => setForm({ ...form, subject })}
							value={form?.subject}
							autoFocus
							maxLength={50}
							showMaxLength
						/>
					</>
				)}

				{isEditingModalOpen === 'severity' && (
					<>
						<TemplateDropdown
							name="Severity"
							options={Object.keys(SeverityMapping).map(
								(key) => SeverityMapping[key],
							)}
							onChange={(index, severity) => {
								setForm({ ...form, severity });
							}}
							value={form?.severity}
						/>
					</>
				)}
				{isEditingModalOpen === 'category' && (
					<>
						<BetaAttributes
							field={{
								label: 'Category',
								type: 'category',
								key: 'categoryId',
								moduleId: ticketsModule?.id,
								options: { module_key: 'default:ticket' },
							}}
							form={{
								categoryId: { value: form?.categoryId },
							}}
							onChange={(index, value) => {
								setForm({ ...form, categoryId: value?.value || value });
							}}
						/>
					</>
				)}
				{isEditingModalOpen === 'location' && (
					<>
						<BetaAttributes
							field={{
								label: 'Location',
								type: 'location',
								key: 'locationId',
							}}
							form={{
								locationId: { value: form?.locationId },
							}}
							onChange={(index, value, _, rawLocation) => {
								setForm({ ...form, locationId: value });
							}}
						/>
					</>
				)}
				<div className="flex justify-end mt-4">
					<Button
						disabled={isUpdateDisabled()}
						onClick={() =>
							onUpdate(
								{
									id: open?.id,
									...(ticket?.description !== form?.description
										? { description: form?.description }
										: {}),
									...(ticket?.subject !== form?.subject
										? { subject: form?.subject }
										: {}),
									...(ticket?.status !== form?.status
										? { status: form?.status }
										: {}),
									...(ticket?.status !== form?.status && form?.status === 'done'
										? { completedDate: new Date() }
										: {}),
									...(ticket?.severity !== form?.severity
										? { severity: form?.severity }
										: {}),
									...(ticket?.assigned_to !== form?.assignedTo
										? { assignedTo: form?.assignedTo }
										: {}),
									...(ticket?.category?.id !== form?.categoryId
										? { categoryId: form?.categoryId }
										: {}),
									...(ticket?.location?.id !== form?.locationId
										? { locationId: form?.locationId }
										: {}),
								},
								() => {
									setEditingModalOpen(false);
									refreshTicket({ id: open?.id }, null, { silent: true });
									// Open statistics modal if status changed to done
									if (
										ticket?.status !== form?.status &&
										form?.status === 'done'
									) {
										openStatistics({ entityType: 'ticket', id: open?.id });
									}
								},
							)
						}
					>
						{updating === 'pending' ? 'Updating...' : 'Update'}
					</Button>
				</div>
			</StackableModal>
		</>
	);
};

export default TicketOverview;
